// src/pages/Team.js
import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Eye, EyeOff, Shield, User } from 'lucide-react';
import { Modal } from './Tables';

const EMPTY = { username:'', email:'', password:'', role:'staff', is_active:1 };

const ROLE_INFO = {
  admin: { label:'Admin', color:'#2563eb', bg:'#eff6ff', desc:'Full access to all features' },
  staff: { label:'Staff', color:'#d97706', bg:'#fef3c7', desc:'Dashboard, Tables, Reports & own Settings only' },
};

const STAFF_ACCESS = [
  { icon:'📊', label:'Dashboard', allowed: true },
  { icon:'🎱', label:'Tables & Sessions', allowed: true },
  { icon:'📈', label:'Reports', allowed: true },
  { icon:'⚙️', label:'Settings (own credentials)', allowed: true },
  { icon:'🍟', label:'Menu Management', allowed: false },
  { icon:'📦', label:'Inventory', allowed: false },
  { icon:'👤', label:'Customers', allowed: false },
  { icon:'👥', label:'Team Management', allowed: false },
];

export default function Team() {
  const { user }               = useAuth();
  const [members, setMembers]  = useState([]);
  const [modal,   setModal]    = useState(null);
  const [selected,setSelected] = useState(null);
  const [form,    setForm]     = useState(EMPTY);
  const [showPwd, setShowPwd]  = useState(false);

  const load = useCallback(async () => {
    try { const r = await api.get('/users'); setMembers(Array.isArray(r.data) ? r.data : []); }
    catch { toast.error('Failed to load team'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(EMPTY); setShowPwd(false); setModal('add'); };
  const openEdit = (m) => {
    setSelected(m);
    setForm({ username: m.username, email: m.email, password:'', role: m.role, is_active: m.is_active ? 1 : 0 });
    setShowPwd(false);
    setModal('edit');
  };

  const save = async () => {
    if (!form.username.trim()) return toast.error('Username required');
    if (!form.email.trim())    return toast.error('Email required');
    if (modal === 'add' && form.password.length < 6) return toast.error('Password must be at least 6 characters');
    try {
      if (modal === 'add') {
        await api.post('/users', form);
        toast.success('Team member added');
      } else {
        await api.put('/users?id=' + selected.id, form);
        toast.success('Team member updated');
      }
      setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  const del = async (m) => {
    if (m.id === user?.id) return toast.error('Cannot delete your own account');
    if (!window.confirm(`Remove ${m.username} from the team?`)) return;
    try { await api.delete('/users?id=' + m.id); toast.success('Member removed'); load(); }
    catch (e) { toast.error(e.response?.data?.error || 'Error'); }
  };

  const admins = members.filter(m => m.role === 'admin');
  const staff  = members.filter(m => m.role === 'staff');

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Team Management</h2>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={16}/> Add Member</button>
      </div>

      {/* Stats */}
      <div className="inv-stats-row" style={{ marginBottom: 20 }}>
        <div className="inv-stat"><div className="inv-stat-value">{members.length}</div><div className="inv-stat-label">Total Members</div></div>
        <div className="inv-stat"><div className="inv-stat-value" style={{ color:'#2563eb' }}>{admins.length}</div><div className="inv-stat-label">Admins</div></div>
        <div className="inv-stat"><div className="inv-stat-value" style={{ color:'#d97706' }}>{staff.length}</div><div className="inv-stat-label">Staff</div></div>
        <div className="inv-stat"><div className="inv-stat-value" style={{ color:'#22c55e' }}>{members.filter(m=>m.is_active).length}</div><div className="inv-stat-label">Active</div></div>
      </div>

      {/* Staff permissions reference */}
      <div className="card" style={{ marginBottom: 20, background:'#fafafa' }}>
        <div className="card-header"><Shield size={16}/> <span>Staff Member Access — What they can and cannot do</span></div>
        <div style={{ display:'flex', flexWrap:'wrap', gap: 8, padding:'4px 0' }}>
          {STAFF_ACCESS.map(a => (
            <div key={a.label} style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 12px', borderRadius:20, fontSize:12, fontWeight:500, background: a.allowed ? '#f0fdf4' : '#fef2f2', color: a.allowed ? '#15803d' : '#b91c1c', border:`1px solid ${a.allowed ? '#bbf7d0' : '#fca5a5'}` }}>
              {a.icon} {a.label} {a.allowed ? '✓' : '✗'}
            </div>
          ))}
        </div>
      </div>

      {/* Members Table */}
      <div className="card">
        <table className="data-table">
          <thead>
            <tr><th>Member</th><th>Email</th><th>Role</th><th>Status</th><th>Joined</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {members.map(m => (
              <tr key={m.id} style={{ background: !m.is_active ? '#fafafa' : '', opacity: m.is_active ? 1 : 0.6 }}>
                <td>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:36, height:36, borderRadius:'50%', background: m.role==='admin' ? '#eff6ff' : '#fef3c7', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:15, color: m.role==='admin' ? '#2563eb' : '#d97706', flexShrink:0 }}>
                      {m.username[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight:600 }}>{m.username} {m.id === user?.id && <span style={{ fontSize:11, color:'#94a3b8' }}>(you)</span>}</div>
                    </div>
                  </div>
                </td>
                <td style={{ color:'#64748b', fontSize:13 }}>{m.email}</td>
                <td>
                  <span style={{ background: ROLE_INFO[m.role].bg, color: ROLE_INFO[m.role].color, padding:'3px 10px', borderRadius:12, fontSize:12, fontWeight:600 }}>
                    {m.role === 'admin' ? '👑' : '👤'} {ROLE_INFO[m.role].label}
                  </span>
                </td>
                <td>
                  {m.is_active
                    ? <span className="badge badge-active">Active</span>
                    : <span className="badge badge-ended">Inactive</span>}
                </td>
                <td style={{ fontSize:12, color:'#94a3b8' }}>{new Date(m.created_at).toLocaleDateString('en-IN')}</td>
                <td>
                  <div style={{ display:'flex', gap:6 }}>
                    <button className="btn btn-outline btn-sm" onClick={() => openEdit(m)}><Pencil size={13}/></button>
                    {m.id !== user?.id && (
                      <button className="btn btn-danger btn-sm" onClick={() => del(m)}><Trash2 size={13}/></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {members.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign:'center', color:'#94a3b8', padding:40 }}>No team members yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {(modal === 'add' || modal === 'edit') && (
        <Modal title={modal === 'add' ? 'Add Team Member' : 'Edit Team Member'} onClose={() => setModal(null)}>
          <div className="form-row">
            <div className="form-group">
              <label>Username</label>
              <input autoFocus value={form.username}
                onChange={e => setForm(f => ({...f, username: e.target.value}))}
                placeholder="e.g. john_staff"/>
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={form.email}
                onChange={e => setForm(f => ({...f, email: e.target.value}))}
                placeholder="john@example.com"/>
            </div>
          </div>

          <div className="form-group">
            <label>{modal === 'add' ? 'Password' : 'New Password (leave blank to keep current)'}</label>
            <div className="input-with-icon">
              <input
                type={showPwd ? 'text' : 'password'}
                value={form.password}
                onChange={e => setForm(f => ({...f, password: e.target.value}))}
                placeholder={modal === 'add' ? 'Min. 6 characters' : 'Leave blank to keep unchanged'}
              />
              <button type="button" className="input-eye" onClick={() => setShowPwd(v => !v)}>
                {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Role</label>
              <select value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))}>
                <option value="staff">👤 Staff — Limited access</option>
                <option value="admin">👑 Admin — Full access</option>
              </select>
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={form.is_active} onChange={e => setForm(f => ({...f, is_active: parseInt(e.target.value)}))}>
                <option value={1}>Active</option>
                <option value={0}>Inactive (cannot login)</option>
              </select>
            </div>
          </div>

          {/* Role description */}
          <div style={{ background: form.role==='admin' ? '#eff6ff' : '#fef9c3', border:`1px solid ${form.role==='admin'?'#bfdbfe':'#fde68a'}`, borderRadius:8, padding:'10px 14px', fontSize:13, color: form.role==='admin'?'#1d4ed8':'#92400e', marginBottom:4 }}>
            {form.role === 'admin'
              ? '👑 Admin: Full access to all features including inventory, menu, customers and team management.'
              : '👤 Staff: Can view dashboard, manage tables/sessions, view reports, and change their own password only.'}
          </div>

          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={save}>
              {modal === 'add' ? <><Plus size={14}/> Add Member</> : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
